import React from "react";
import TextBlock from "../TextBlock";
import Text from "../Text";
import ProTagSVG from "@/public/assets/images/pro-tag.svg";
import {
  ContentType,
  ItemContentCardType,
  ItemContentImageType,
  ItemContentTextType,
  ItemType,
} from "@/sections/Roles";
import Button from "../Button";
import { transformText } from "@/utils/transformText";
import CustomImage from "../CustomImage";
import GradientLightText from "../GradientLightText";
import WalletIcon from "@/public/assets/images/wallet.svg";

type RolesContentProps = {
  content: ContentType;
  activeFilter: string;
  setActiveFilter: (value: string) => void;
};

const RolesContent = ({
  content,
  activeFilter,
  setActiveFilter,
}: RolesContentProps) => {
  const renderComponent = (item: ItemType) => {
    switch (item.type) {
      case "card":
        return (
          <RolesCard
            content={item.content as ItemContentCardType}
            item={item}
          />
        );
      case "image":
        return (
          <RolesImage
            content={item.content as ItemContentImageType}
            item={item}
          />
        );
      case "text":
        return (
          <RolesText
            content={item.content as ItemContentTextType}
            item={item}
          />
        );
    }
  };

  const filteredItems = content.items.filter((filter) => {
    if (activeFilter === "all") {
      return filter;
    }
    if (activeFilter === "pro") {
      return filter.isPro === true;
    }

    if (activeFilter === "basic") {
      return filter.isPro === false;
    }
  });
  return (
    <>
      <TextBlock className="text-block--wider">
        <Text element="h2" elementStyle="h1">
          {content.title}
        </Text>
        <Text element="p" elementStyle="subtitle">
          {content.body}
        </Text>
      </TextBlock>
      <div className="roles__filters">
        <Text element="p" elementStyle="h2" fontFamily="noto-serif">
          {content.exploreTheRoles.title}
        </Text>
        <ul className="roles__filters-list">
          {content.exploreTheRoles.filters.map((filter) => (
            <li className="roles__filters-item" key={filter.value}>
              <button
                className={`roles__filters-button ${
                  activeFilter === filter.value
                    ? "roles__filters-button--active"
                    : ""
                }`.trim()}
                onClick={() => {
                  setActiveFilter(filter.value);
                }}
              >
                {filter.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <ul className="roles__list">
        {filteredItems?.map((item) => (
          <li key={item.id} className={`roles__item roles__item--${item.size}`}>
            {item.isPro && item.type === "card" ? (
              <div className="roles__item-tag">
                <ProTagSVG />
              </div>
            ) : null}
            {renderComponent(item)}
          </li>
        ))}
      </ul>
    </>
  );
};

type RolesCardProps = {
  item: ItemType;
  content: ItemContentCardType;
};

const RolesCard = ({ item, content }: RolesCardProps) => {
  return (
    <div className="roles__card">
      <div className="roles__card-image-holder">
        <CustomImage
          alt={content.image.alt}
          src={content.image.src.desktop}
          srcTablet={content.image.src.tablet}
          srcMobile={content.image.src.mobile}
        />
        {!!content.income ? (
          <div className="roles__card-image-content">
            <div className="roles__card-image-title">
              <WalletIcon />
              <Text
                element="p"
                elementStyle="body-secondary"
                className="weight-700"
              >
                <GradientLightText element="span">
                  {content.income.title}
                </GradientLightText>
              </Text>
            </div>
            <Text
              element="p"
              elementStyle="body-secondary"
              className="weight-700"
            >
              {content.income.text}
            </Text>
          </div>
        ) : null}
      </div>
      <div className="roles__card-content">
        <div className="roles__card-content-top">
          <Text
            element="h3"
            elementStyle="subtitle"
            className="weight-700 margin-bottom-10"
          >
            <GradientLightText element="span">
              {content.title}
            </GradientLightText>
          </Text>
          <Text
            element="p"
            elementStyle="body-secondary"
            className="weight-500"
          >
            {content.body}
          </Text>
        </div>
        <div>
          <Text
            element="p"
            elementStyle="body-secondary"
            className="weight-700 orange margin-bottom-10"
          >
            {transformText(content.bodySecondary, {
              br: (chunk) => <br />,
            })}
          </Text>
          <Button type="secondary" isTop isLink href={content.button.link}>
            {content.button.text}
          </Button>
        </div>
      </div>
    </div>
  );
};

type RolesImageProps = {
  item: ItemType;
  content: ItemContentImageType;
};

const RolesImage = ({ item, content }: RolesImageProps) => {
  return (
    <div className="roles__image">
      <CustomImage
        alt={content.image.alt}
        src={content.image.src.desktop}
        srcTablet={content.image.src.tablet}
        srcMobile={content.image.src.mobile}
      />
    </div>
  );
};

type RolesTextProps = {
  item: ItemType;
  content: ItemContentTextType;
};

const RolesText = ({ item, content }: RolesTextProps) => {
  return <div>card</div>;
};

export default RolesContent;
